import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
    Mic, Square, Upload, Plus, Pencil, Trash2, ChevronDown, ChevronRight, FileText, Sparkles, Loader2,
    Paperclip, ImageIcon, X, Tag as TagIcon,
    Wrench, AlertTriangle, ListChecks, FolderOpen, FileSignature, Archive,
} from "lucide-react";
import { toast } from "sonner";
import {
    listWiki, createWiki, updateWiki, deleteWiki, dictateWiki,
    uploadWikiAttachment, deleteWikiAttachment, wikiAttachmentUrl,
} from "@/lib/api";
import { germanDate, WIKI_CATEGORIES } from "@/lib/format";
import { SearchBox, useFulltextFilter, MatchCount } from "@/components/SearchBox";
import SuchAuswahl from "@/components/ui/such-auswahl";

const emptyDraft = { title: "", tag: "", category: "sonstiges", content: "", summary: "", original_transcript: "" };

const CATEGORY_ICONS = {
    "handwerker-kontakte": Wrench,
    "schaeden-reparaturen": AlertTriangle,
    "prozesse-ablaeufe": ListChecks,
    "objekt-doku": FolderOpen,
    "verkauf-notariat": FileSignature,
    "sonstiges": Archive,
};

/**
 * Reusable Wiki panel used by
 *  - /wiki               (scope="global", scopeId=null)
 *  - /immobilien/:id     (scope="property", scopeId=property.id)
 *  - /einheiten/:id      (Wiki der übergeordneten Immobilie)
 *
 * Die Einträge holt die Komponente normalerweise selbst. Bringt der Aufrufer
 * sie über `entries` mit — so macht es die Immobilien-Detailseite, die die
 * Liste ohnehin für das Verkauft-Badge und die Fotos-Timeline braucht —, wird
 * nicht erneut geladen; Änderungen melden sich dann über `onChanged` nach oben.
 */
export default function WikiPanel({
    scope,
    scopeId = null,
    scopeLabel = "",
    highlightEntry = null,
    entries: providedEntries = null,
    onChanged = null,
}) {
    const [fetchedEntries, setFetchedEntries] = useState([]);
    const [fetching, setFetching] = useState(true);

    const controlled = providedEntries !== null;
    const entries = controlled ? providedEntries : fetchedEntries;
    const loading = controlled ? false : fetching;
    const [expanded, setExpanded] = useState({});
    const [showOriginal, setShowOriginal] = useState({});
    const [highlighted, setHighlighted] = useState(null);
    const [collapsedCats, setCollapsedCats] = useState({});
    const [activeTag, setActiveTag] = useState(null);

    // Draft dialog (used for manual create, edit, and post-dictate preview)
    const [draftOpen, setDraftOpen] = useState(false);
    const [draft, setDraft] = useState(emptyDraft);
    const [editingId, setEditingId] = useState(null);
    const [saving, setSaving] = useState(false);
    const [aiBanner, setAiBanner] = useState(false); // true when draft came from KI

    // Recording state
    const [recording, setRecording] = useState(false);
    const [recSeconds, setRecSeconds] = useState(0);
    const [processing, setProcessing] = useState(false);
    const mediaRecorderRef = useRef(null);
    const chunksRef = useRef([]);
    const timerRef = useRef(null);
    const streamRef = useRef(null);

    const uploadInputRef = useRef(null);

    // Nach jeder Änderung (Speichern, Löschen, Anhang) aufgerufen. Im
    // kontrollierten Fall lädt der Aufrufer neu, sonst diese Komponente selbst.
    const load = useCallback(async () => {
        if (controlled) {
            if (onChanged) await onChanged();
            return;
        }
        setFetching(true);
        try {
            const params = scope === "property" ? { scope: "property", property_id: scopeId } : { scope: "global" };
            setFetchedEntries(await listWiki(params));
        } finally {
            setFetching(false);
        }
    }, [controlled, onChanged, scope, scopeId]);

    useEffect(() => {
        if (!controlled) load();
    }, [controlled, load]);

    // Deep-Link vom Telegram-Bot: ?entry=<id> → aufklappen + hervorheben + scrollen
    useEffect(() => {
        if (!highlightEntry || entries.length === 0) return;
        const hit = entries.find((e) => e.id === highlightEntry);
        if (!hit) return;
        setExpanded((x) => ({ ...x, [highlightEntry]: true }));
        setHighlighted(highlightEntry);
        // scrollen sobald DOM aktualisiert ist
        setTimeout(() => {
            const el = document.querySelector(`[data-testid="wiki-entry-${highlightEntry}"]`);
            if (el && el.scrollIntoView) el.scrollIntoView({ behavior: "smooth", block: "center" });
        }, 150);
        // Hervorhebung nach 3 s wegblenden
        const t = setTimeout(() => setHighlighted(null), 3000);
        return () => clearTimeout(t);
    }, [highlightEntry, entries]);

    // Fulltext search
    const searchFields = useMemo(() => ["title", "tag", "content", "summary"], []);
    const { query, setQuery, filtered, total, matches } = useFulltextFilter(entries, searchFields);

    // Tag-Filter-Chips + Kategorie-Gruppierung
    const tagCounts = useMemo(() => {
        const m = {};
        entries.forEach((e) => { if (e.tag) m[e.tag] = (m[e.tag] || 0) + 1; });
        return Object.entries(m).sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0], "de"));
    }, [entries]);
    const visible = useMemo(
        () => (activeTag ? filtered.filter((e) => (e.tag || "") === activeTag) : filtered),
        [filtered, activeTag]
    );
    const grouped = useMemo(() => {
        const g = {};
        visible.forEach((e) => {
            const c = WIKI_CATEGORIES.some((x) => x.value === e.category) ? e.category : "sonstiges";
            (g[c] = g[c] || []).push(e);
        });
        return g;
    }, [visible]);

    // --- Recording ---
    const startRecording = async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            streamRef.current = stream;
            const mimeType = MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
                ? "audio/webm;codecs=opus"
                : (MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm" : "");
            const rec = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
            chunksRef.current = [];
            rec.ondataavailable = (e) => { if (e.data && e.data.size > 0) chunksRef.current.push(e.data); };
            rec.onstop = async () => {
                streamRef.current?.getTracks().forEach((t) => t.stop());
                const blob = new Blob(chunksRef.current, { type: mimeType || "audio/webm" });
                await sendToBackend(blob, "diktat.webm");
            };
            rec.start();
            mediaRecorderRef.current = rec;
            setRecSeconds(0);
            setRecording(true);
            timerRef.current = setInterval(() => setRecSeconds((s) => s + 1), 1000);
        } catch (err) {
            console.error(err);
            toast.error("Mikrofon-Zugriff verweigert – bitte im Browser erlauben.");
        }
    };

    const stopRecording = () => {
        try {
            mediaRecorderRef.current?.stop();
        } catch (e) { /* ignore */ }
        clearInterval(timerRef.current);
        setRecording(false);
    };

    const onUploadFile = async (file) => {
        if (!file) return;
        if (file.size > 25 * 1024 * 1024) { toast.error("Datei zu groß (max. 25 MB)"); return; }
        await sendToBackend(file, file.name);
    };

    const sendToBackend = async (blob, filename) => {
        setProcessing(true);
        try {
            toast.info("Transkribiere und strukturiere Diktat …", { duration: 4000 });
            const preview = await dictateWiki(blob, scope, scopeId, filename);
            setDraft({
                title: preview.title || "",
                tag: preview.tag || "",
                category: preview.category || "sonstiges",
                content: preview.content || "",
                summary: preview.summary || "",
                original_transcript: preview.original_transcript || "",
            });
            setEditingId(null);
            setAiBanner(true);
            setDraftOpen(true);
        } catch (err) {
            console.error(err);
            const detail = err?.response?.data?.detail || err.message || "Unbekannter Fehler";
            toast.error(`Diktat fehlgeschlagen: ${detail}`);
        } finally {
            setProcessing(false);
        }
    };

    // --- CRUD ---
    const openCreate = () => {
        setDraft(emptyDraft);
        setEditingId(null);
        setAiBanner(false);
        setDraftOpen(true);
    };

    const openEdit = (e) => {
        setDraft({
            title: e.title || "",
            tag: e.tag || "",
            category: e.category || "sonstiges",
            content: e.content || "",
            summary: e.summary || "",
            original_transcript: e.original_transcript || "",
        });
        setEditingId(e.id);
        setAiBanner(false);
        setDraftOpen(true);
    };

    const saveDraft = async () => {
        if (!draft.title.trim()) { toast.error("Titel ist erforderlich"); return; }
        if (!draft.content.trim()) { toast.error("Inhalt ist erforderlich"); return; }
        setSaving(true);
        try {
            const payload = {
                scope,
                scope_id: scope === "property" ? scopeId : null,
                title: draft.title.trim(),
                tag: (draft.tag || "").trim().toLowerCase() || null,
                content: draft.content,
                summary: (draft.summary || "").trim() || null,
                original_transcript: draft.original_transcript || null,
            };
            if (editingId) {
                await updateWiki(editingId, payload);
                toast.success("Wiki-Eintrag aktualisiert");
            } else {
                await createWiki(payload);
                toast.success("Wiki-Eintrag gespeichert");
            }
            setDraftOpen(false);
            setDraft(emptyDraft);
            setEditingId(null);
            setAiBanner(false);
            load();
        } finally { setSaving(false); }
    };

    const onDelete = async (id) => {
        if (!window.confirm("Diesen Wiki-Eintrag wirklich löschen?")) return;
        await deleteWiki(id);
        toast.success("Eintrag gelöscht");
        load();
    };

    const fmtTime = (s) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;

    return (
        <div className="space-y-4">
            {/* Action Bar */}
            <div className="bg-white border border-gray-200 rounded-md p-3 flex items-center gap-3 flex-wrap">
                {!recording ? (
                    <Button
                        size="sm"
                        onClick={startRecording}
                        disabled={processing}
                        className="bg-rose-600 hover:bg-rose-700 text-white"
                        data-testid="wiki-btn-record"
                    >
                        <Mic size={14} className="mr-1.5" /> Diktieren
                    </Button>
                ) : (
                    <Button
                        size="sm"
                        onClick={stopRecording}
                        className="bg-slate-900 hover:bg-slate-800 text-white animate-pulse"
                        data-testid="wiki-btn-stop"
                    >
                        <Square size={12} className="mr-1.5" /> Stop · {fmtTime(recSeconds)}
                    </Button>
                )}

                <input
                    type="file"
                    ref={uploadInputRef}
                    accept="audio/mp3,audio/mpeg,audio/wav,audio/webm,audio/m4a,audio/mp4,.mp3,.wav,.webm,.m4a,.mp4"
                    className="hidden"
                    onChange={(e) => onUploadFile(e.target.files?.[0])}
                    data-testid="wiki-upload-input"
                />
                <Button
                    size="sm"
                    variant="outline"
                    onClick={() => uploadInputRef.current?.click()}
                    disabled={recording || processing}
                    data-testid="wiki-btn-upload"
                >
                    <Upload size={14} className="mr-1.5" /> Audio hochladen (mp3/wav/webm)
                </Button>

                <Button
                    size="sm"
                    variant="outline"
                    onClick={openCreate}
                    disabled={recording || processing}
                    data-testid="wiki-btn-new"
                >
                    <Plus size={14} className="mr-1.5" /> Manueller Eintrag
                </Button>

                {processing && (
                    <span className="inline-flex items-center gap-2 text-xs text-slate-600">
                        <Loader2 size={14} className="animate-spin" /> KI arbeitet …
                    </span>
                )}

                <div className="ml-auto flex items-center gap-3">
                    <SearchBox value={query} onChange={setQuery} placeholder="Suche Titel, Tag, Inhalt…" testid="wiki-search" className="w-[280px]" />
                    <MatchCount matches={matches} total={total} label="Einträge" labelSingular="Eintrag" />
                </div>
            </div>

            {/* Tag-Filter-Chips */}
            {tagCounts.length > 0 && (
                <div className="flex items-center gap-1.5 flex-wrap" data-testid="wiki-tag-chips">
                    <TagIcon size={12} className="text-gray-400" />
                    {tagCounts.map(([tag, count]) => (
                        <button
                            key={tag}
                            onClick={() => setActiveTag(activeTag === tag ? null : tag)}
                            className={`text-[11px] px-2 py-0.5 rounded-full border transition-colors ${
                                activeTag === tag
                                    ? "bg-slate-900 text-white border-slate-900"
                                    : "bg-white text-slate-600 border-gray-200 hover:border-slate-400"
                            }`}
                            data-testid={`wiki-tag-chip-${tag}`}
                        >
                            {tag} <span className="opacity-60">{count}</span>
                        </button>
                    ))}
                    {activeTag && (
                        <button onClick={() => setActiveTag(null)} className="text-[11px] text-rose-600 hover:underline ml-1" data-testid="wiki-tag-reset">
                            Filter aufheben
                        </button>
                    )}
                </div>
            )}

            {/* Entries */}
            {loading ? (
                <div className="p-10 text-center text-gray-400 text-sm">Lade Einträge…</div>
            ) : visible.length === 0 ? (
                <div className="bg-white border border-gray-200 rounded-md p-10 text-center text-gray-400 text-sm">
                    {entries.length === 0
                        ? "Noch keine Wiki-Einträge. Klick auf »Diktieren«, um deine erste Notiz aufzunehmen."
                        : "Keine Einträge passen zur Suche."}
                </div>
            ) : (
                <div className="space-y-5">
                    {WIKI_CATEGORIES.filter((c) => (grouped[c.value] || []).length > 0).map((cat) => {
                        const list = grouped[cat.value];
                        const collapsed = collapsedCats[cat.value];
                        const CatIcon = CATEGORY_ICONS[cat.value] || Archive;
                        return (
                            <div key={cat.value} data-testid={`wiki-category-${cat.value}`}>
                                <button
                                    className="w-full flex items-center gap-2 mb-2"
                                    onClick={() => setCollapsedCats((x) => ({ ...x, [cat.value]: !collapsed }))}
                                    data-testid={`wiki-category-toggle-${cat.value}`}
                                >
                                    {collapsed ? <ChevronRight size={14} className="text-gray-400" /> : <ChevronDown size={14} className="text-gray-400" />}
                                    <CatIcon size={14} className="text-slate-500" />
                                    <span className="text-[11px] uppercase tracking-[0.18em] font-semibold text-slate-600">{cat.label}</span>
                                    <span className="text-[10px] bg-gray-100 text-gray-500 rounded-full px-1.5 py-0.5 tabular-nums">{list.length}</span>
                                    <span className="flex-1 border-t border-gray-200 ml-2" />
                                </button>
                                {!collapsed && (
                                <div className="space-y-3">
                    {list.map((e) => {
                        const isOpen = expanded[e.id];
                        return (
                            <div
                                key={e.id}
                                className={
                                    "bg-white border rounded-md transition-all " +
                                    (highlighted === e.id ? "border-emerald-500 ring-2 ring-emerald-300 shadow-md" : "border-gray-200")
                                }
                                data-testid={`wiki-entry-${e.id}`}
                            >
                                <div className="flex items-start gap-3 px-4 py-3">
                                    <button
                                        className="mt-1 text-gray-400 hover:text-slate-900"
                                        onClick={() => setExpanded((x) => ({ ...x, [e.id]: !isOpen }))}
                                        data-testid={`wiki-toggle-${e.id}`}
                                    >
                                        {isOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                                    </button>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <h3 className="font-semibold text-slate-900 truncate">{e.title}</h3>
                                            {e.tag && (
                                                <span className="text-[10px] uppercase tracking-wider bg-slate-900 text-white rounded-full px-2 py-0.5">
                                                    {e.tag}
                                                </span>
                                            )}
                                            {e.original_transcript && (
                                                <span className="text-[10px] bg-emerald-50 text-emerald-800 border border-emerald-200 rounded px-1.5 py-0.5 inline-flex items-center gap-1">
                                                    <Sparkles size={9} /> Diktiert
                                                </span>
                                            )}
                                            {e.attachments && e.attachments.length > 0 && (
                                                <span className="text-[10px] bg-sky-50 text-sky-800 border border-sky-200 rounded px-1.5 py-0.5 inline-flex items-center gap-1">
                                                    <Paperclip size={9} /> {e.attachments.length}
                                                </span>
                                            )}
                                        </div>
                                        {e.summary && !isOpen && (
                                            <div className="text-xs text-gray-600 mt-1 line-clamp-2">{e.summary}</div>
                                        )}
                                        <div className="text-[11px] text-gray-400 mt-1">
                                            Zuletzt bearbeitet: {germanDate(e.updated_at)}
                                        </div>
                                    </div>
                                    <Button variant="ghost" size="sm" onClick={() => openEdit(e)} data-testid={`wiki-edit-${e.id}`}>
                                        <Pencil size={12} className="mr-1" /> Bearbeiten
                                    </Button>
                                    <Button variant="ghost" size="sm" onClick={() => onDelete(e.id)} className="text-rose-600 hover:text-rose-700" data-testid={`wiki-delete-${e.id}`}>
                                        <Trash2 size={12} />
                                    </Button>
                                </div>
                                {isOpen && (
                                    <div className="px-11 pb-4 space-y-3">
                                        <MarkdownView content={e.content} />

                                        <AttachmentSection entry={e} onChanged={load} />

                                        {e.original_transcript && (
                                            <div className="mt-4">
                                                <button
                                                    className="text-[11px] uppercase tracking-wider text-gray-500 hover:text-slate-900 inline-flex items-center gap-1"
                                                    onClick={() => setShowOriginal((x) => ({ ...x, [e.id]: !x[e.id] }))}
                                                >
                                                    <FileText size={11} /> Original-Diktat {showOriginal[e.id] ? "ausblenden" : "anzeigen"}
                                                </button>
                                                {showOriginal[e.id] && (
                                                    <div className="mt-2 p-3 rounded bg-gray-50 border border-gray-200 text-xs text-gray-700 whitespace-pre-wrap leading-relaxed">
                                                        {e.original_transcript}
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                                </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Draft / Preview dialog */}
            <Dialog open={draftOpen} onOpenChange={(v) => { if (!saving) setDraftOpen(v); }}>
                <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>
                            {editingId ? "Wiki-Eintrag bearbeiten" : (aiBanner ? "KI-Vorschau · Bitte prüfen und speichern" : "Neuer Wiki-Eintrag")}
                        </DialogTitle>
                        <DialogDescription className="text-xs">
                            {scope === "property" ? `Objekt-bezogen: ${scopeLabel}` : "Globaler Eintrag (App-weit sichtbar)"}
                        </DialogDescription>
                    </DialogHeader>
                    {aiBanner && (
                        <div className="rounded-md bg-emerald-50 border border-emerald-200 px-3 py-2 text-xs text-emerald-900 inline-flex items-center gap-2">
                            <Sparkles size={12} /> KI hat Titel, Tag und Struktur vorgeschlagen. Du kannst alles frei bearbeiten, bevor du speicherst.
                        </div>
                    )}
                    <div className="grid grid-cols-3 gap-3 py-2">
                        <div className="col-span-2">
                            <Label>Titel *</Label>
                            <Input value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })} data-testid="wiki-input-title" />
                        </div>
                        <div>
                            <Label>Tag (Freitext)</Label>
                            <Input
                                value={draft.tag}
                                onChange={(e) => setDraft({ ...draft, tag: e.target.value.toLowerCase() })}
                                placeholder="z.B. handwerker, heizung"
                                data-testid="wiki-input-tag"
                            />
                        </div>
                        <div>
                            <Label>Kategorie</Label>
                            <SuchAuswahl wert={draft.category || "sonstiges"} onWaehlen={(v) => setDraft({ ...draft, category: v })}
                                optionen={[
                                    ...WIKI_CATEGORIES.map((c) => ({ wert: c.value, text: c.label })),
                                ]}
                                testId="wiki-input-category"
                            />
                        </div>
                        <div className="col-span-2">
                            <Label>Kurzfassung (1 Satz, optional)</Label>
                            <Input value={draft.summary} onChange={(e) => setDraft({ ...draft, summary: e.target.value })} data-testid="wiki-input-summary" />
                        </div>
                        <div className="col-span-3">
                            <Label>Inhalt (Markdown) *</Label>
                            <Textarea
                                value={draft.content}
                                onChange={(e) => setDraft({ ...draft, content: e.target.value })}
                                rows={14}
                                className="font-mono text-xs"
                                data-testid="wiki-input-content"
                            />
                            <div className="text-[10px] text-gray-500 mt-1">Markdown: `## Überschrift`, `- Bullet`, `**fett**`, `*kursiv*`</div>
                        </div>
                        {draft.original_transcript && (
                            <div className="col-span-3">
                                <Label className="text-gray-500 text-xs">Original-Transkript (wird mit gespeichert)</Label>
                                <div className="mt-1 p-3 rounded bg-gray-50 border border-gray-200 text-xs text-gray-700 whitespace-pre-wrap leading-relaxed max-h-40 overflow-y-auto">
                                    {draft.original_transcript}
                                </div>
                            </div>
                        )}
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDraftOpen(false)} disabled={saving} data-testid="wiki-btn-cancel">
                            Verwerfen
                        </Button>
                        <Button onClick={saveDraft} disabled={saving} className="bg-slate-900 hover:bg-slate-800" data-testid="wiki-btn-save">
                            {saving ? "Speichere…" : (editingId ? "Aktualisieren" : "Speichern")}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

/** Sehr schmaler Markdown-Renderer (Headings, Bulletlisten, Fett/Kursiv, Line breaks) */
function MarkdownView({ content }) {
    // Nur die Basics – wir wollen keine schwere lib einbinden.
    const lines = (content || "").split("\n");
    const out = [];
    let listBuffer = [];
    const flushList = () => {
        if (listBuffer.length > 0) {
            out.push(<ul key={`ul-${out.length}`} className="list-disc pl-5 space-y-1 my-2 text-sm text-slate-800">
                {listBuffer.map((li, i) => <li key={i} dangerouslySetInnerHTML={{ __html: inlineFormat(li) }} />)}
            </ul>);
            listBuffer = [];
        }
    };
    lines.forEach((ln, idx) => {
        const trim = ln.trim();
        if (trim.startsWith("- ") || trim.startsWith("* ")) {
            listBuffer.push(trim.replace(/^[-*]\s+/, ""));
        } else {
            flushList();
            if (trim.startsWith("### ")) {
                out.push(<h4 key={idx} className="text-sm font-semibold text-slate-900 mt-3 mb-1">{trim.slice(4)}</h4>);
            } else if (trim.startsWith("## ")) {
                out.push(<h3 key={idx} className="text-base font-bold text-slate-900 mt-4 mb-1">{trim.slice(3)}</h3>);
            } else if (trim.startsWith("# ")) {
                out.push(<h2 key={idx} className="text-lg font-bold text-slate-900 mt-4 mb-2">{trim.slice(2)}</h2>);
            } else if (trim.length === 0) {
                out.push(<div key={idx} className="h-2" />);
            } else {
                out.push(<p key={idx} className="text-sm text-slate-800 leading-relaxed" dangerouslySetInnerHTML={{ __html: inlineFormat(trim) }} />);
            }
        }
    });
    flushList();
    return <div>{out}</div>;
}

function inlineFormat(str) {
    // very small: **bold**, *italic*, `code`, escape < >
    const esc = str.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
    return esc
        .replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>")
        .replace(/(?<!\*)\*([^*]+)\*(?!\*)/g, "<em>$1</em>")
        .replace(/`([^`]+)`/g, "<code class='px-1 py-0.5 bg-gray-100 rounded text-[11px] font-mono'>$1</code>");
}


/** Anhänge (Fotos + PDFs) für einen Wiki-Eintrag anzeigen, hochladen und löschen. */
function AttachmentSection({ entry, onChanged }) {
    const [uploading, setUploading] = useState(false);
    const fileRef = useRef(null);
    const attachments = entry.attachments || [];

    const onUpload = async (files) => {
        if (!files || files.length === 0) return;
        setUploading(true);
        try {
            for (const f of Array.from(files)) {
                if (f.size > 20 * 1024 * 1024) {
                    toast.error(`${f.name} ist zu groß (max. 20 MB)`);
                    continue;
                }
                try {
                    await uploadWikiAttachment(entry.id, f);
                } catch (err) {
                    const detail = err?.response?.data?.detail || err.message;
                    toast.error(`Upload «${f.name}» fehlgeschlagen: ${detail}`);
                }
            }
            toast.success("Anhang hochgeladen");
            onChanged && onChanged();
        } finally {
            setUploading(false);
            if (fileRef.current) fileRef.current.value = "";
        }
    };

    const onDelete = async (att) => {
        if (!window.confirm(`Anhang "${att.filename}" wirklich entfernen?`)) return;
        await deleteWikiAttachment(att.id);
        toast.success("Anhang entfernt");
        onChanged && onChanged();
    };

    return (
        <div className="mt-4 border-t border-gray-100 pt-3">
            <div className="flex items-center justify-between mb-2">
                <div className="text-[11px] uppercase tracking-wider text-gray-500 inline-flex items-center gap-1">
                    <Paperclip size={11} /> Anhänge{attachments.length ? ` (${attachments.length})` : ""}
                </div>
                <div>
                    <input
                        ref={fileRef}
                        type="file"
                        multiple
                        accept="image/*,application/pdf,.pdf,.jpg,.jpeg,.png,.gif,.webp,.heic"
                        className="hidden"
                        onChange={(e) => onUpload(e.target.files)}
                        data-testid={`att-upload-${entry.id}`}
                    />
                    <button
                        onClick={() => fileRef.current?.click()}
                        disabled={uploading}
                        className="text-xs px-2 py-1 rounded border border-gray-200 hover:bg-gray-50 inline-flex items-center gap-1"
                        data-testid={`att-upload-btn-${entry.id}`}
                    >
                        {uploading ? (
                            <><Loader2 size={11} className="animate-spin" /> Lade …</>
                        ) : (
                            <><Upload size={11} /> Datei anhängen</>
                        )}
                    </button>
                </div>
            </div>
            {attachments.length === 0 ? (
                <div className="text-[11px] text-gray-400">Keine Anhänge. Fotos, Screenshots und PDFs bis 20 MB.</div>
            ) : (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                    {attachments.map((a) => (
                        <AttachmentThumb key={a.id} att={a} onDelete={() => onDelete(a)} />
                    ))}
                </div>
            )}
        </div>
    );
}

function AttachmentThumb({ att, onDelete }) {
    const isImage = (att.content_type || "").startsWith("image/");
    const [previewOpen, setPreviewOpen] = useState(false);
    const url = wikiAttachmentUrl(att.id);
    return (
        <div className="group relative border border-gray-200 rounded-md overflow-hidden bg-gray-50">
            {isImage ? (
                <button
                    onClick={() => setPreviewOpen(true)}
                    className="block w-full aspect-video bg-white"
                    data-testid={`att-thumb-${att.id}`}
                    title={att.filename}
                >
                    <img
                        src={url}
                        alt={att.filename}
                        className="w-full h-full object-cover"
                        loading="lazy"
                    />
                </button>
            ) : (
                <a
                    href={url}
                    target="_blank"
                    rel="noreferrer"
                    className="flex items-center gap-2 p-3"
                    data-testid={`att-doc-${att.id}`}
                >
                    <FileText size={20} className="text-rose-600 shrink-0" />
                    <div className="min-w-0">
                        <div className="text-xs font-semibold text-slate-900 truncate">{att.filename}</div>
                        <div className="text-[10px] text-gray-500">{Math.max(1, Math.round((att.size || 0) / 1024))} KB · PDF</div>
                    </div>
                </a>
            )}
            {isImage && (
                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/60 to-transparent px-2 py-1 flex items-center justify-between opacity-0 group-hover:opacity-100 transition-opacity">
                    <div className="text-[10px] text-white truncate">{att.filename}</div>
                </div>
            )}
            <button
                onClick={onDelete}
                className="absolute top-1 right-1 bg-white/90 hover:bg-rose-100 text-rose-700 rounded p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                title="Anhang entfernen"
                data-testid={`att-delete-${att.id}`}
            >
                <X size={12} />
            </button>

            {previewOpen && isImage && (
                <div
                    className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
                    onClick={() => setPreviewOpen(false)}
                    data-testid={`att-preview-${att.id}`}
                >
                    <img src={url} alt={att.filename} className="max-h-[90vh] max-w-[90vw] object-contain" />
                </div>
            )}
        </div>
    );
}
